import React, { useState, useEffect, useRef } from "react";
import { aiService } from "../services/aiService";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../supabaseClient";
import "./LegalAssistant.css";

interface LegalAssistantProps {
  isFullScreen?: boolean;
  onBackToDashboard?: () => void;
  onCollapse?: () => void;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const LegalAssistant: React.FC<LegalAssistantProps> = ({
  isFullScreen = false,
  onBackToDashboard,
  onCollapse,
}) => {
  const { user, signOut } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false); // Prevent duplicate streaming
  const [windowWidth, setWindowWidth] = useState(() => {
    // Load saved width from localStorage or use default
    const saved = localStorage.getItem("legalAssistantWidth");
    return saved ? parseInt(saved, 10) : 400;
  });
  const [isResizing, setIsResizing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);


  // Resize functionality
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const minWidth = 300;
      const maxWidth = 800;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWindowWidth(newWidth);
        localStorage.setItem("legalAssistantWidth", newWidth.toString());
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    if (isResizing) {
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (user) {
      loadChatHistory();
    }
  }, [user]);

  useEffect(() => {
    // Only scroll when not typing or at the end of typing
    if (!typingMessageId) {
      scrollToBottom();
    }
  }, [messages, typingMessageId]);

  const saveChatHistory = async (messages: Message[]) => {
    if (!user) return;

    try {
      // Convert Message[] to ChatMessage[] format expected by aiService
      const chatMessages = messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.getTime(),
      }));

      // Save to localStorage via aiService
      localStorage.setItem(
        `chat_history_${user.id}`,
        JSON.stringify({
          userId: user.id,
          messages: chatMessages,
          lastUpdated: Date.now(),
          context: {},
        })
      );
    } catch (error) {
      console.error("Error saving chat history:", error);
    }
  };

  const loadChatHistory = async () => {
    if (!user) return;

    const history = aiService.getChatHistory(user.id);

    if (history.length === 0) {
      const welcomeContent = `Hello! I'm your Legal AI Research Assistant. I can help you with:

• Researching immigration policies and requirements
• Analyzing case-specific compliance issues
• Finding relevant legal precedents and case law
• Providing guidance on visa applications and processes
• Generating comprehensive client and case summaries

All queries are automatically processed through our advanced Research Mode for accurate, up-to-date legal information.

You can also type "clear" to start a fresh conversation anytime.

What legal research question can I help you with today?`;

      // Use streaming for the welcome message too
      await addAssistantMessage(welcomeContent);
    } else {
      const formattedHistory: Message[] = history.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
      }));

      setMessages(formattedHistory);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addAssistantMessage = async (content: string) => {
    // Prevent duplicate streaming calls
    if (isStreaming) {
      console.warn("Streaming already in progress, ignoring duplicate call");
      return;
    }

    setIsStreaming(true);

    // CRITICAL: Always hide thinking bubble before starting to stream
    setIsLoading(false);

    const assistantMessage: Message = {
      id: Date.now().toString(),
      role: "assistant",
      // Start with non-empty placeholder so the bubble isn't blank if streaming fails early
      content: "…",
      timestamp: new Date(),
    };

    // Add the message to the list immediately
    setMessages((prev) => {
      const newMessages = [...prev, assistantMessage];
      saveChatHistory(newMessages);
      return newMessages;
    });

    // Mark this message as typing
    setTypingMessageId(assistantMessage.id);

    // Scroll once at the beginning
    setTimeout(() => scrollToBottom(), 100);

    let streamErrored = false;
    try {
      // ChatGPT-style word-by-word streaming (25% faster than original)
      const words = content.split(" ");
      let displayedContent = "";

      for (let i = 0; i < words.length; i++) {
        // Add the current word
        displayedContent += (i > 0 ? " " : "") + words[i];

        // Update the message with accumulated content
        setMessages((prev) => {
          return prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, content: displayedContent }
              : msg
          );
        });

        // 25% faster streaming - reduced base delays (15% faster than current 10% increase)
        let delay = 107; // Was 126, now 15% faster (126 * 0.85 = 107)
        const word = words[i];

        // Smart pauses for natural reading rhythm (all 15% faster than current)
        if (word.endsWith(".") || word.endsWith("!") || word.endsWith("?")) {
          delay = 383; // Was 450, now 383 (450 * 0.85 = 382.5)
        } else if (
          word.endsWith(",") ||
          word.endsWith(":") ||
          word.endsWith(";")
        ) {
          delay = 191; // Was 225, now 191 (225 * 0.85 = 191.25)
        } else if (word.includes("\n")) {
          delay = 230; // Was 270, now 230 (270 * 0.85 = 229.5)
        } else if (word.length > 10) {
          delay = 138; // Was 162, now 138 (162 * 0.85 = 137.7)
        } else if (word.length <= 2) {
          delay = 69; // Was 81, now 69 (81 * 0.85 = 68.85)
        }

        // Minimal randomness to avoid jitter (±8ms only)
        delay += Math.random() * 16 - 8;
        delay = Math.max(61, delay); // Was 72, now 61 (72 * 0.85 = 61.2)

        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      // Final update and save to history
      setMessages((prev) => {
        const updatedMessages = prev.map((msg) =>
          msg.id === assistantMessage.id ? { ...msg, content: content } : msg
        );
        saveChatHistory(updatedMessages);
        return updatedMessages;
      });
    } catch (error) {
      streamErrored = true;
      console.error("Error during streaming:", error);
      // Ensure we at least display the full content if something broke mid-stream
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantMessage.id ? { ...m, content } : m))
      );
    } finally {
      // Always clean up streaming state
      setTypingMessageId(null);
      setIsStreaming(false);
      setTimeout(() => scrollToBottom(), 200);

      // Final safety: if after streaming the message is still placeholder or empty, force set full content
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id === assistantMessage.id) {
            if (!m.content || m.content === "…") {
              return { ...m, content };
            }
          }
          return m;
        })
      );

      // Persist after safety adjustment
      setTimeout(() => {
        setMessages((prev) => {
          const updated = [...prev];
          saveChatHistory(updated);
          return updated;
        });
      }, 50);
      if (streamErrored) {
        console.warn("Streaming finalized with fallback content assignment");
      }
    }
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleClearChat = async () => {
    setMessages([]);
    localStorage.removeItem(`chat_history_${user?.id}`);
    setShowClearConfirmation(false);

    // Add a fresh welcome message with streaming
    const welcomeContent = `Hello! I'm your Legal AI Research Assistant. I can help you with:

• Researching immigration policies and requirements
• Analyzing case-specific compliance issues
• Finding relevant legal precedents and case law
• Providing guidance on visa applications and processes
• Generating comprehensive client and case summaries

All queries are automatically processed through our advanced Research Mode for accurate, up-to-date legal information.

You can also type "clear" to start a fresh conversation anytime.

What legal research question can I help you with today?`;

    await addAssistantMessage(welcomeContent);
  };

  const handleCancelClear = () => {
    setShowClearConfirmation(false);
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Regular Enter - submit the form
      e.preventDefault();
      if (!inputValue.trim() || isLoading || isStreaming) return;

      // Create a synthetic form event and call handleSubmit
      const syntheticEvent = {
        preventDefault: () => {},
        currentTarget: e.currentTarget.form,
        target: e.currentTarget.form,
      } as unknown as React.FormEvent;

      handleSubmit(syntheticEvent);
    }
    // Shift+Enter - allow default behavior (new line)
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading || isStreaming) return;

    const currentInput = inputValue; // Store before clearing
    setInputValue("");

    // Check for special commands BEFORE adding to chat
    if (
      currentInput.toLowerCase() === "clear" ||
      currentInput.toLowerCase().includes("clear chat")
    ) {
      setShowClearConfirmation(true);
      return;
    }

    // Add user message to chat for non-clear commands
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: currentInput,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // Show "Thinking..." until streaming starts
    setIsLoading(true);

    try {
      // Route all queries to Research Mode
      const response = await aiService.handleUserResearchQuery(user!.id, currentInput);

      // addAssistantMessage will handle setIsLoading(false) and streaming
      await addAssistantMessage(response);
    } catch (error) {
      console.error("Error handling message:", error);
      const errorContent = `❌ Error: ${
        error instanceof Error ? error.message : "Something went wrong"
      }`;
      // addAssistantMessage will handle setIsLoading(false) and streaming
      await addAssistantMessage(errorContent);
    } finally {
      // Ensure loading is always turned off (safety net)
      setIsLoading(false);
    }
  };




  // Professional formatting transformer: converts plaintext / light markdown
  // into structured legal-professional styled JSX. Pure presentation.
  const filterMessageContent = (raw: string): React.ReactNode => {
    // 1. Strip internal meta comments
    let content = raw.replace(/<!--DOWNLOAD_URLS:\[.*?\]-->/g, "").trim();
    // 2. Normalize newlines
    content = content.replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n");

    const SECTION_HEADINGS = [
      "summary",
      "next steps",
      "sources",
      "analysis",
      "recommendations",
      "compliance analysis",
      "risk signals",
      "requirements",
      "actions",
      "notes",
    ];
    const sectionRegex = new RegExp(
      `^(${SECTION_HEADINGS.join("|")})\\s*:`,
      "i"
    );

    const escapeHtml = (s: string) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const inlineFormat = (s: string) => {
      s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      s = s.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
      s = s.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
      s = s.replace(
        /\[([^\]]+)\]\((https?:[^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="assistant-link">$1</a>'
      );
      return s;
    };
    const classifyLine = (s: string) => {
      if (/❌|critical/i.test(s)) return "critical";
      if (/⚠️|warning|risk/i.test(s)) return "warning";
      if (/✅|success|ok|passed/i.test(s)) return "success";
      return undefined;
    };

    const lines = content.split("\n");
    const nodes: React.ReactNode[] = [];
    let ul: string[] = [];
    let ol: string[] = [];
    type PillMode = "risk signals" | "requirements" | null;
    let pillMode: PillMode = null;
    let pillItems: string[] = [];

    const flushUL = () => {
      if (!ul.length) return;
      nodes.push(
        <ul className="assistant-list" key={`ul-${nodes.length}`}>
          {ul.map((t, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: t }} />
          ))}
        </ul>
      );
      ul = [];
    };
    const flushOL = () => {
      if (!ol.length) return;
      nodes.push(
        <ol className="assistant-list ordered" key={`ol-${nodes.length}`}>
          {ol.map((t, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: t }} />
          ))}
        </ol>
      );
      ol = [];
    };
    const flushPills = () => {
      if (!pillMode || !pillItems.length) return;
      // Deduplicate & normalize
      const seen = new Set<string>();
      const cleaned: string[] = [];
      pillItems.forEach((item) => {
        const trimmed = item.replace(/[.;]+$/, "").trim();
        if (!trimmed) return;
        let norm = trimmed.toLowerCase();
        norm = norm
          .replace(/\([^)]*\)/g, "")
          .replace(
            /\b(might indicate|seems|potentially|currently|only|yet)\b/g,
            ""
          )
          .replace(/[^a-z0-9 ]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (norm.includes("future application date"))
          norm = "future application date";
        if (!seen.has(norm) && norm.length > 2) {
          seen.add(norm);
          cleaned.push(trimmed);
        }
      });
      nodes.push(
        <div
          key={`pill-${nodes.length}`}
          className={`assistant-section ${pillMode.replace(/\s+/g, "-")}`}
        >
          <div className="assistant-heading-line">
            <span className="assistant-heading">
              {pillMode.replace(/\b\w/g, (c) => c.toUpperCase())}:
            </span>
          </div>
          <div className="pill-list">
            {cleaned.map((p, i) => {
              const severity =
                /missing|incomplete|no |not provided|critical|error|failed/i.test(
                  p
                )
                  ? "critical"
                  : /pending|limited|unclear|potential|future/i.test(p)
                  ? "warning"
                  : "info";
              return (
                <span key={i} className={`pill ${severity}`}>
                  {p}
                </span>
              );
            })}
          </div>
        </div>
      );
      pillMode = null;
      pillItems = [];
    };

    for (let i = 0; i < lines.length; i++) {
      const rawLine = lines[i];
      const line = rawLine.trimEnd();
      if (!line.trim()) {
        flushUL();
        flushOL();
        flushPills();
        nodes.push(
          <div
            key={`sp-${nodes.length}`}
            className="assistant-spacer"
            aria-hidden="true"
          />
        );
        continue;
      }

      // Markdown heading (#, ##, ###)
      const md = /^(#{1,3})\s+(.+)/.exec(line);
      if (md) {
        flushUL();
        flushOL();
        flushPills();
        const text = inlineFormat(
          escapeHtml(md[2].replace(/^#+\s*/, "").replace(/:$/, ""))
        );
        const level = md[1].length;
        nodes.push(
          <div
            key={`mdh-${nodes.length}`}
            className={`assistant-md-heading h${level}`}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        );
        // If this heading corresponds to pill sections with no trailing text, enable pill mode
        const lower = text.toLowerCase();
        if (lower === "risk signals" || lower === "requirements") {
          pillMode = lower as PillMode;
        }
        continue;
      }

      // Explicit section heading with colon
      const sectionMatch = sectionRegex.exec(line);
      if (sectionMatch) {
        flushUL();
        flushOL();
        flushPills();
        const label = sectionMatch[1].toLowerCase();
        const after = line.substring(sectionMatch[0].length).trim();
        if (label === "risk signals" || label === "requirements") {
          pillMode = label as PillMode;
          if (after) {
            pillItems.push(after);
          }
          // If after has semicolons treat as immediate flush list
          if (after && /;/.test(after)) {
            pillItems.push(...after.split(/;+/).slice(1));
          }
          continue;
        }
        // Normal section heading
        nodes.push(
          <p
            key={`sec-${nodes.length}`}
            className="assistant-paragraph"
            dangerouslySetInnerHTML={{
              __html: `<span class=\"assistant-heading\">${label.toUpperCase()}: </span>${inlineFormat(
                escapeHtml(after)
              )}`,
            }}
          />
        );
        continue;
      }

      // List items
      const ulMatch = /^([•\-*])\s+(.+)/.exec(line);
      if (ulMatch) {
        flushOL();
        const item = inlineFormat(escapeHtml(ulMatch[2]));
        if (pillMode) {
          pillItems.push(ulMatch[2]);
        } else {
          ul.push(item);
        }
        continue;
      }
      const olMatch = /^(\d{1,3})[.)]\s+(.+)/.exec(line);
      if (olMatch) {
        flushUL();
        const item = inlineFormat(escapeHtml(olMatch[2]));
        if (pillMode) {
          pillItems.push(olMatch[2]);
      } else {
          ol.push(item);
        }
        continue;
      }

      // If in pill mode accumulate; treat semicolon-separated phrases too
      if (pillMode) {
        if (/;/.test(line)) {
          pillItems.push(...line.split(/;+/));
        } else {
          pillItems.push(line);
        }
        // If next line is a blank or new heading flush
        const next = lines[i + 1];
        if (
          !next ||
          !next.trim() ||
          sectionRegex.test(next) ||
          /^(#{1,3})\s+/.test(next)
        ) {
          flushPills();
        }
        continue;
      }

      // Normal paragraph
      let html = inlineFormat(escapeHtml(line));
      html = html.replace(
        /([A-Za-z][A-Za-z0-9 _]{1,40}?):\s*(\b[^\n]{2,120}?)(?=$|<br\/?>)/g,
        (m, k, v) =>
          `<span class=\"kv-pair\"><strong>${k}:</strong> ${v}</span>`
      );
      const riskClass = classifyLine(line);
      nodes.push(
        <p
          key={`p-${nodes.length}`}
          className={`assistant-paragraph${
            riskClass ? " risk-tag " + riskClass : ""
          }`}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    flushUL();
    flushOL();
    flushPills();
    return <div className="assistant-formatted">{nodes}</div>;
  };




  return (
    <div
      className="legal-assistant"
      style={{
        width: windowWidth,
        position: "fixed",
        right: 0,
        top: 0,
        height: "100vh",
        zIndex: 1000,
      }}
    >
      {/* Resize handle */}
      <div
        ref={resizeRef}
        onMouseDown={handleResizeStart}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "5px",
          cursor: "ew-resize",
          backgroundColor: "transparent",
          zIndex: 1001,
        }}
        onMouseEnter={(e) => {
          if (!isResizing) {
            e.currentTarget.style.backgroundColor = "rgba(102, 126, 234, 0.3)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isResizing) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      />

      {/* Header - Different for full-screen vs embedded */}
      {isFullScreen ? (
        // Full-screen header with full branding and user controls
        <div
          className="top-bar"
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(229, 231, 235, 0.3)",
            borderRadius: "16px 16px 0 0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div className="top-bar-left">
            <h1 className="logo" style={{ color: "#000000" }}>
              CaseKaizen
            </h1>
            <h3 className="assistant-title" style={{ color: "#000000" }}>
              Legal AI Assistant
            </h3>
            <button
              className="dock-toggle-btn"
              onClick={() => {
                if (onBackToDashboard) {
                  onBackToDashboard();
                }
              }}
            >
              📊 Dashboard
            </button>
          </div>
          <div className="top-bar-right">
            <div className="user-profile">
              <span className="user-avatar">👤</span>
              <span className="user-name">
                {user?.user_metadata?.full_name ||
                  user?.user_metadata?.name ||
                  user?.user_metadata?.username ||
                  user?.email?.split("@")[0] ||
                  "User"}
              </span>
              <span className="dropdown-arrow">▼</span>
              <div className="user-dropdown">
                <button onClick={signOut}>Sign Out</button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Embedded header - simplified with just the title and close button
        <div
          className="embedded-header"
          style={{
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(229, 231, 235, 0.3)",
            borderRadius: "16px 16px 0 0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
          }}
        >
          {onCollapse ? (
            <button
              onClick={onCollapse}
              title="Collapse Assistant"
              style={{
                background: "rgba(102, 126, 234, 0.1)",
                border: "1px solid rgba(102, 126, 234, 0.2)",
                color: "#667eea",
                padding: "4px 8px",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.15)",
                transition: "all 0.2s ease",
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          ) : (
            <div></div>
          )}
          <h3
            className="embedded-title"
            style={{ color: "#000000", margin: 0 }}
          >
            Legal AI Assistant
          </h3>
          <div></div>
        </div>
      )}

      <div className="assistant-body" style={{ position: "relative", flex: 1 }}>
        {/* Clear Chat Confirmation Overlay (covers only chat area) */}
        {showClearConfirmation && (
          <div
            className="confirmation-overlay"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirmation-dialog">
              <div className="confirmation-content">
                <h3>Clear Chat History?</h3>
                <p>
                  This will delete all your conversation history. This action
                  cannot be undone.
                </p>
                <div className="confirmation-buttons">
                  <button className="confirm-btn" onClick={handleClearChat}>
                    Clear Chat
                  </button>
                  <button className="cancel-btn" onClick={handleCancelClear}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className="chat-messages"
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(229, 231, 235, 0.3)",
          borderBottom: "none",
          borderRadius: "0",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          flex: "1",
          overflowY: "auto",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.role}`}>
            <div className="message-content">
              <div
                className={`message-text ${
                    message.role === "assistant" &&
                    message.id === typingMessageId
                    ? "typing"
                    : ""
                }`}
              >
                {filterMessageContent(message.content)}
              </div>
            </div>
            <div className="message-timestamp">
              {message.timestamp.toLocaleTimeString()}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message assistant">
            <div className="message-content">
              <div className="loading-indicator">Thinking...</div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>


      <div
        className="chat-input-container"
        style={{
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(229, 231, 235, 0.3)",
          borderRadius: "0 0 16px 16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          borderTop: "1px solid rgba(229, 231, 235, 0.3)",
          padding: "20px 24px",
        }}
      >
        <form onSubmit={handleSubmit} className="chat-input-form">
            <div className="chat-input-row">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message... (Shift+Enter for new line)"
                disabled={isLoading}
                className="chat-input"
                rows={1}
                style={{
                  resize: "none",
                  fontFamily: "inherit",
                  lineHeight: "1.4",
                }}
              />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="send-btn"
              title={
                isLoading
                  ? "Processing..."
                  : inputValue.trim()
                  ? "Send message"
                  : "Type a message to send"
              }
            >
              {isLoading ? "⏳" : "➤"}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

export default LegalAssistant;
